import type { ChatbotBlock } from "blocks/v2/Chatbot/schema";
import { usePageContext } from "lib/context/page-context";
import { useMemo } from "react";
import {
  CHATBOT_BLOCK_FIELD_NAME_VALUE,
  CHATBOT_BLOCK_FIELD_DEFAULT_MESSAGE_VALUE,
  CHATBOT_BLOCK_FIELD_DEFAULT_PROMPT_VALUE,
  CHATBOT_BLOCK_FIELD_DIRECTIVE_VALUE,
  CHATBOT_BLOCK_FIELD_DISCLOSURE_VALUE,
  CHATBOT_BLOCK_FIELD_DATASOURCE_NO_RESULTS_VALUE,
} from "./constants";

export function useChatConfig(config: ChatbotBlock["config"]) {
  const { accountId } = usePageContext();

  return useMemo(
    () => ({
      blockId: config.id ?? "",
      chatbotName: config.chatbotName ?? CHATBOT_BLOCK_FIELD_NAME_VALUE,
      dataSources: config.dataSources ?? [],
      dataSourcesMode: "chatgpt",
      dataSourcesNoResultsMode:
        config.dataSourcesNoResultsMode ??
        CHATBOT_BLOCK_FIELD_DATASOURCE_NO_RESULTS_VALUE,
      dataSourcesNoResultsMessage:
        config.dataSourcesNoResultsMessage ??
        CHATBOT_BLOCK_FIELD_DATASOURCE_NO_RESULTS_VALUE,
      defaultMessage:
        config.defaultMessage ?? CHATBOT_BLOCK_FIELD_DEFAULT_MESSAGE_VALUE,
      defaultPrompt:
        config.defaultPrompt ?? CHATBOT_BLOCK_FIELD_DEFAULT_PROMPT_VALUE,
      directive: config.directive ?? CHATBOT_BLOCK_FIELD_DIRECTIVE_VALUE,
      displayDisclosure: config.displayDisclosure ?? false,
      disclosureText:
        config.disclosureText ?? CHATBOT_BLOCK_FIELD_DISCLOSURE_VALUE,
      isStaticMode: config.defaultMessageMode === "static",
      model: config.model,
      placeholder: config.placeholder,
      temperature: config.temperature,
      triggers: (config.triggers ?? []).map(
        (trigger) => trigger.config as Required<typeof trigger.config>
      ),
      user: accountId?.toString(),
    }),
    [
      config.id,
      config.chatbotName,
      config.dataSources,
      config.dataSourcesNoResultsMode,
      config.dataSourcesNoResultsMessage,
      config.defaultMessage,
      config.defaultPrompt,
      config.directive,
      config.displayDisclosure,
      config.disclosureText,
      config.defaultMessageMode,
      config.model,
      config.placeholder,
      config.temperature,
      config.triggers,
      accountId,
    ]
  );
}
